import { clsx } from "clsx";
import type { KeyboardEvent, MouseEvent } from "react";
import { useTranslations } from "../../i18n";
import { Checkbox } from "../Checkbox";
import { useTableContext } from "./context";
import { TableCell } from "./TableCell";

type Props<Item> = {
  item: Item;
  className?: string;
};

export function TableRow<Item>({ item, className }: Props<Item>) {
  const { t } = useTranslations();
  const {
    columns,
    onRenderCell,
    onRowClick,
    selection,
    emptyState,
    gridTemplateColumns,
  } = useTableContext<Item>();

  const handleClick = () => {
    if (onRowClick) {
      onRowClick(item);
    }
  };

  const handleCheckboxClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    // Only handle keyboard events when the row itself is focused
    if (e.currentTarget !== e.target) {
      return;
    }

    if ((e.key === "Enter" || e.key === " ") && onRowClick) {
      e.preventDefault();
      onRowClick(item);
    }
  };

  const showSelection = !!selection && !emptyState;
  const isSelected = !!selection?.isItemSelected(item);

  return (
    <div
      className={clsx(
        "nuco-table__row",
        onRowClick && "nuco-table__row--clickable",
        className,
      )}
      style={{
        gridTemplateColumns,
      }}
      role="row"
      tabIndex={onRowClick ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={onRowClick ? "Clickable row" : undefined}
    >
      {showSelection && (
        <div className="nuco-table__row-checkbox-box" role="gridcell">
          <Checkbox
            checked={isSelected}
            onChange={selection.onSelectRow(item)}
            onClick={handleCheckboxClick}
            size="sm"
            aria-label={t("ui.table.selectRowAriaLabel")}
          />
        </div>
      )}
      {columns.map((column) => {
        const cellContent = onRenderCell
          ? onRenderCell(column, item)
          : ((item as any)[column.key] ?? "");

        return (
          <TableCell key={column.key} align={column.align}>
            {cellContent}
          </TableCell>
        );
      })}
    </div>
  );
}
